import styled from '@emotion/styled'
import React, { useEffect, useRef, useState } from 'react'
import Div100vh from 'react-div-100vh'

import PreviewPanel from './PreviewPanel'
import SelectorPanel from './SelectorPanel'
import './index.css'

const blockSVG = 'https://cdn.july.com/personalisation/personalisation/new/BLOCK.svg'
const boldSVG = 'https://cdn.july.com/personalisation/personalisation/new/BOLD.svg'
const bubbleSVG = 'https://cdn.july.com/personalisation/personalisation/new/BUBBLE.svg'
const modernScriptSVG = 'https://cdn.july.com/personalisation/personalisation/new/MODERN-SCRIPT.svg'
const retroSerifSVG = 'https://cdn.july.com/personalisation/personalisation/new/RETRO-SERIF.svg'
const retroSVG = 'https://cdn.july.com/personalisation/personalisation/new/RETRO.svg'
const sansSerifSVG = 'https://cdn.july.com/personalisation/personalisation/new/SANS-SERIF.svg'
const serifSVG = 'https://cdn.july.com/personalisation/personalisation/new/SERIF.svg'
const signatureSVG = 'https://cdn.july.com/personalisation/personalisation/new/SIGNATURE.svg'

function objectExists(array, objToFind) {
  return array.some(obj => {
    // Check that every key in objToFind is in the obj from the array
    // and has the same value.
    return Object.keys(objToFind).every(
      key => obj.hasOwnProperty(key) && obj[key] === objToFind[key]
    );
  });
}



const splitter = (list) => {
  const segmenter = new Intl.Segmenter();
  return [...segmenter.segment(list)].map(val => val.segment);
}

const Emojireg =
  /\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]([0-9]*)$/
const ChineseReg = /(\p{Script=Hani})+/gu

const textTransform = (text, font) => {
  if (
    [
      'Block',
      'Wide',
      'Casual',
      'all-serif',
      'all-sans',
      'Painted',
      'College',
    ].includes(font)
  ) {
    return text.toUpperCase()
  }
  return text
}

const textColorsLuggage = {
  'Lavender': '#90759B',
  'Lilac': '#DCC5DF',
  'Ballet-Pink': '#F1C9DE',
  'Rose-Pink': '#DC75A4',
  'Grenaine': '#9E5360',
  'Blush-Pink': '#E77472',
  'Magenta': '#B12D52',
  'Maroon': '#731A29',
  'Red': '#C92327',
  'Burnt-Orange': '#DC5527',
  'Mango': '#EF7E2E',
  'Rust': '#9B4827',
  'Toffee': '#705037',
  'Taupe': '#D3A289',
  'Butter-Yellow': '#FBF3BE',
  'Yellow': '#E8B629',
  'Lime': '#C6B937',
  'Green': '#4A813C',
  'Forest-Green': '#034123',
  'Mint': '#78AB93',
  'Ice-Blue': '#BEE3E9',
  'Denim': '#75A6B3',
  'Ocean-Blue': '#3E8EA1',
  'Blue': '#1466B2',
  'Grey': '#95979E',
  'Slate-Grey': '#63727E',
  'Black': '#000000',
  White: '#FFFFFF',
}

const textColorsBags = {
  Coral: '#FDC0B8',
  'Dusty-Pink': '#F5DFE2',
  Fuschia: '#DF3C7F',
  Mint: '#D4EECC',
  'Sky-Blue': '#BBE0EE',
  'Aqua-Blue': '#47BCEE',
  'French-Navy': '#182D6D',
  Red: '#C63531',
  Plum: '#AC3856',
  Purple: '#64258E',
  Lavender: '#957EA3',
  'Forest-Green': '#1C4125',
  Green: '#6A9A52',
  Yellow: '#E8BE47',
  Orange: '#E96737',
  White: '#FFFFFF',
  Grey: '#97999D',
  Black: '#000',
}

const ContentContanier = styled.div`
  height: 100%;
  position: relative;
  background: #f8f8f8;
  display: flex;
  align-items: stretch;
  flex-direction: column;
  overflow: hidden;
  z-index: 999999999999;

  @media (min-width: 801px) {
    flex-direction: row-reverse;
  }
`

const fontData = {
  Block: {
    name: 'Block',
    uppercase: true,
    numbers: false,
    letterLimit: 5,
    displayImage: blockSVG,
  },
  Retro: {
    name: 'Retro',
    uppercase: false,
    numbers: true,
    letterLimit: 10,
    displayImage: retroSVG,
  },
  Serif: {
    name: 'Serif',
    uppercase: false,
    numbers: true,
    letterLimit: 10,
    displayImage: serifSVG,
  },
  Cursive: {
    name: 'Cursive',
    uppercase: false,
    numbers: true,
    letterLimit: 10,
    displayImage: modernScriptSVG,
  },
  'love-letter': {
    name: 'Love Letter',
    uppercase: false,
    numbers: true,
    letterLimit: 12,
    displayImage: modernScriptSVG,
  },
  Signature: {
    name: 'Signature',
    uppercase: false,
    numbers: true,
    letterLimit: 20,
    displayImage: signatureSVG,
  },
  'all-sans': {
    name: 'Carry All Sans',
    uppercase: true,
    numbers: false,
    letterLimit: 5,
    displayImage: sansSerifSVG,
  },
  'all-serif': {
    name: 'Carry All Serif',
    uppercase: true,
    numbers: false,
    letterLimit: 5,
    displayImage: serifSVG,
  },
  'bold-new': {
    name: 'Bold',
    uppercase: false,
    numbers: false,
    letterLimit: 5,
    displayImage: boldSVG
  },
  'retro-serif': {
    name: 'Retro Serif',
    uppercase: false,
    numbers: false,
    letterLimit: 10,
    displayImage: retroSerifSVG
  },
  'decorative': {
    name: 'Decorative',
    uppercase: false,
    numbers: false,
    letterLimit: 10,
    displayImage: modernScriptSVG
  },
  'sans-serif': {
    name: 'Sans Serif',
    uppercase: false,
    numbers: false,
    letterLimit: 10,
    displayImage: sansSerifSVG
  },
  'bubble': {
    name: 'Bubble',
    uppercase: false,
    numbers: false,
    letterLimit: 10,
    displayImage: bubbleSVG
  },
  'sans-serif-embos': {
    name: 'sans-serif-embos',
    uppercase: false,
    numbers: false,
    letterLimit: 10,
    displayImage: sansSerifSVG
  },
  'serif-embos': {
    name: 'serif-embos',
    uppercase: false,
    numbers: true,
    letterLimit: 10,
    displayImage: serifSVG,
  },

}

const decorativeColours = {
  'Lilac': ['#C2AECD', '#826A80'],
  'Blue': ['#A0D0D7', '#75A6B3'],
  'Pink': ['#C36C89', '#8B4655'],
  'Yellow': ['#E8DF8E', '#C99B3E'],
  'White': ['#FFFFFF', '#95979E'],
  'Black': ['#000000', '#63727E']
}

const embosColours = {
  'Gold': '#FFD700',
  'Silver': '#c0c0c0',
  'Blind': '#212a2c',
}

const printFonts = [
  'bold-new',
  'bubble',
  'retro-serif',
  'sans-serif',
  'Retro',
  'Cursive',
  'Block',
  'Signature',
  'Serif'
]

const bagFonts = [
  'all-serif',
  'all-sans',
]

const embosFonts = [
  'sans-serif-embos',
  'serif-embos'
]

export const returnMatchingPersonalisation = (caseLookupValue) => {
  try {
    if (localStorage.previous_personalisation !== undefined) {
      let allPersonalisation = JSON.parse(localStorage.previous_personalisation)

      const selectedCaseData = caseLookupValue
      const isPrint = selectedCaseData?.isLuggage || selectedCaseData?.isMe
      const isEmbos = selectedCaseData?.isEmbos
      let fonts =
        isEmbos ?
          embosFonts : (isPrint ? printFonts : bagFonts)

      let colours = isEmbos ? embosColours : Object.keys(isPrint ? textColorsLuggage : textColorsBags)
      let locations = selectedCaseData?.locations
        ? Object.keys(selectedCaseData?.locations)
        : []

      let matchPersonalisation = []
      for (let pers of allPersonalisation) {
        if (
          locations.includes(pers.position) &&
          fonts.includes(pers.font) &&
          colours.includes(pers.color) &&
          (pers.shadow !== undefined ? colours.includes(pers.shadow) : true)
        ) {
          matchPersonalisation.push(pers)
        }
      }

      return matchPersonalisation.slice(0, 10);
    }
  } catch (e) {
    console.log(e)
  }
  return []
}

function isAlphanumeric(str) {
  return /^[a-zA-Z0-9]+$/.test(str);
}


const PersonalisationModal = ({
  handleClose,
  handleSave,
  initialValues,
  timeframe,
  productData
}) => {
  const [previousPers] = useState(returnMatchingPersonalisation(productData))

  const caseSize = productData.name;
  const initialiseValues = (param) =>
    initialValues[param]
      ? initialValues[param]
      : productData?.defaultValues[param]

  const [changed, setChanged] = useState(
    initialValues && initialValues['text'] !== undefined
  )
  const [font, setFont] = useState(initialiseValues('font'))
  const [color, setColor] = useState(initialiseValues('color'))
  const [shadow, setShadow] = useState(initialiseValues('shadow'))
  const [location, setLocation] = useState(initialiseValues('location'))
  const [text, setText] = useState(initialiseValues('text'))
  const [textFull, setTextFull] = useState(
    initialValues && initialValues['text'] !== undefined
      ? initialiseValues('text')
      : ''
  )
  const [accepted, setAccepted] = useState(
    initialValues && initialValues['text'] !== undefined
  )
  const [language, setLanguage] = useState('english')
  const [colorType, setColorType] = useState(0)
  const [completionAmount, setCompletionAmount] = useState(-1)
  const [selectedStep, setSelectedStep] = useState(undefined)
  const [displayStep, setDisplayStep] = useState(undefined)
  const [completedSteps, setCompletedSteps] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
  })

  let textColors = productData.fontColors.colors
  const locationData = productData.locations[location]

  const maxLetters = Math.min(
    locationData.maxLetters,
    fontData[font] ? fontData[font]['letterLimit'] : 7
  )
  const uppercase = fontData[font] ? fontData[font].uppercase : false

  const [currentFontData, setCurrentFontData] = useState(fontData)

  useEffect(() => {
    setColorType(0)
    if (changed === true) {
      handleTextChange({ currentTarget: { value: textFull } })
    }
    const newFontData = {}
    Object.keys(fontData).map((key) => {
      const value = fontData[key]
      newFontData[key] = {
        ...value,
        letterLimit: Math.min(value.letterLimit, locationData.maxLetters),
      }
    })
    setCurrentFontData(newFontData)
  }, [font, location])

  const saveMiddleware = (labelData) => {
    try {
      if (localStorage.previous_personalisation !== undefined) {
        let previousPersonalisation = JSON.parse(localStorage.previous_personalisation)
        if (!objectExists(previousPersonalisation, labelData)) {
          previousPersonalisation = [labelData, ...previousPersonalisation].slice(0, 25);
          localStorage.setItem('previous_personalisation', JSON.stringify(previousPersonalisation))
        }
      } else {
        let previousPersonalisation = [labelData]
        localStorage.setItem('previous_personalisation', JSON.stringify(previousPersonalisation))
      }
    } catch (e) {
      console.log(e)
    }
    handleSave(labelData)
  }

  const handleAddToCart = async () => {
    if (changed) {
      text === ''
        ? saveMiddleware(initialValues)
        : saveMiddleware({
          text: textTransform(text, font),
          font: font.toLowerCase(),
          position: location,
          color,
          ...(['block', 'college', 'heritage'].includes(font.toLowerCase()) ? { shadow } : {}),
        })
    }
  }
  const handleExit = () => {
    handleClose()
  }

  const handleChange = (newV) => {
    setLanguage(newV)
    if (newV === 'chinese') {
      setFont('ZH-BLOCK')
      handleTextChange({ currentTarget: { value: '七月' } })
    } else {
      setFont('retro-serif')
      handleTextChange({ currentTarget: { value: 'JLY' } })
    }
  }

  useEffect(() => {
    const containsChinese = ChineseReg.test(text)
    if (language !== 'chinese' && containsChinese && productData.isLuggage) {
      setFont('ZH-BLOCK')
      setLanguage('chinese')
    }
  }, [])

  const handleTextChange = (e) => {
    if (productData.isEmbos && e.currentTarget.value && !isAlphanumeric(e.currentTarget.value)) {
      return;
    }

    if (changed === false) {
      setChanged(true)
    }
    const newValue = e.currentTarget.value

    const containsChinese = ChineseReg.test(newValue)
    const containsEmoji = Emojireg.test(newValue)
    if (
      !(productData.isLuggage || productData.isMe) &&
      (containsChinese || containsEmoji)
    )
      return

    if (language !== 'chinese' && containsChinese && productData.isLuggage) {
      setFont('ZH-BLOCK')
      setLanguage('chinese')
    }
    if (language === 'chinese' || containsChinese) {
      let EngChars = `${newValue}`
      splitter(newValue)
        .map(
          (val) =>
            (EngChars = EngChars.replace(Emojireg, '').replace(ChineseReg, ''))
        )
      const ChinChars = `${newValue}`.replace(EngChars, '').replace(/\s+/g, '')
      setText((prev) =>
        splitter(ChinChars).length > maxLetters
          ? splitter(ChinChars).slice(0, maxLetters).join('')
          : ChinChars
      )
      setTextFull((prev) =>
        splitter(ChinChars).length > maxLetters
          ? splitter(ChinChars).slice(0, maxLetters).join('')
          : newValue
      )
    } else {
      // if (
      //   /^[A-zÀ-ÿ\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]]+$/.test(newValue) ||
      //   splitter(newValue).length === 0 ||
      //   /(\p{Script=Hani})+/gu.test(newValue)
      // )

      setTextFull((prev) => newValue)
      setText((prev) =>
        splitter(newValue)
          .slice(0, maxLetters)
          .map((val) => (uppercase === true ? val.toUpperCase() : val))
          .join('')
      )
    }
  }

  const handleDone = () => {
    setCompletionAmount((prev) =>
      displayStep > completionAmount ? displayStep : prev
    )
    setCompletedSteps((prev) => {
      let newObj = { ...prev }
      newObj[`${displayStep}`] = true
      return newObj
    })
    setSelectedStep(undefined)
    setDisplayStep(undefined)
  }

  if (font === 'decorative') {
    textColors = decorativeColours
  }

  if (font.includes('embos')) {
    textColors = embosColours
  }

  const perImages = productData.images.map(image => ({
    url: image.url,
    positionName: Object.keys(productData.locations).find(key => productData.locations[key]._id === image.positionId) || '',
  }))

  return (
    <Div100vh>
      <ContentContanier>
        <PreviewPanel
          selectedStep={selectedStep}
          isMe={productData.isMe}
          isBag={productData.isBag}
          handleExit={handleExit}
          accepted={accepted}
          caseSize={caseSize}
          text={text}
          font={font}
          color={color}
          shadow={shadow}
          location={location}
          title=""
          locationData={locationData}
          maxLetters={maxLetters}
          isBottle={productData.isBottle}
          timeframe={timeframe}
          imageSrc={productData?.images?.find(image => image.positionId === locationData?._id)?.url}
        />
        <SelectorPanel
          previousPersonalisation={previousPers}
          caseTypeName={productData.caseTypeName}
          changed={changed}
          maxLetters={maxLetters}
          title={''}
          selectedStep={selectedStep}
          isLuggage={productData.isLuggage}
          completionAmount={completionAmount}
          accepted={accepted}
          completedSteps={completedSteps}
          setSelectedStep={setSelectedStep}
          displayStep={displayStep}
          setDisplayStep={setDisplayStep}
          isPhone={productData.isPhone}
          isMe={productData.isMe}
          isBottle={productData.isBottle}
          isEmbos={productData.isEmbos}
          setFont={setFont}
          font={font}
          text={text}
          textFull={textFull}
          handleTextChange={handleTextChange}
          value={language}
          handleChange={handleChange}
          textColors={textColors}
          setColorType={setColorType}
          setColor={setColor}
          color={color}
          shadow={shadow}
          location={location}
          setLocation={setLocation}
          setAccepted={setAccepted}
          setShadow={setShadow}
          colorType={colorType}
          caseSize={caseSize}
          handleAddToCart={handleAddToCart}
          handleDone={handleDone}
          currentFontData={currentFontData}
          locations={Object.keys(productData.locations)}
          perImages={perImages}
        />
      </ContentContanier>
    </Div100vh>
  )
}

export default PersonalisationModal
